import { useOutletContext } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { briefsQueryOptions } from '@/entities/brief';
import { useProjectCapabilities } from '@/entities/user';
import type { ProjectDetail } from '@/entities/project';
import type { ProjectDetailOutletContext } from '@/pages/ProjectDetail/projectDetail.types';
import { StaggerItem } from '@/shared/ui/animations';
import {
  DetailsSection,
  GetStartedSoon,
  QuickStart,
  useProjectOverview,
} from '@/widgets/project-overview';
import { ProjectSnapshot } from '@/widgets/project-snapshot';
import { useTranslation } from 'react-i18next';

/**
 * Project Detail → Overview section.
 *
 * Composition point for the Overview surface. Pulls the project + share-modal
 * context from the outlet, fetches the members list (for the header avatar
 * stack), then composes:
 *   - widgets/project-detail-header — title strip + members + Share button
 *   - widgets/project-snapshot — header card with status + create-brief CTA
 *   - widgets/project-overview/{QuickStart,GetStartedSoon,DetailsSection}
 *
 * Page composition (cross-WIDGET imports
 * happen here at the page layer per the canonical FSD boundary rule).
 */
/**
 * Open comments on the project, from the snapshot the shell already loaded.
 * The snapshot carries totals; still-open is the difference between them.
 */
function openCommentsOf(project: ProjectDetail): number {
  const comments = project.snapshot?.comments;
  return comments ? comments.total - comments.resolved : 0;
}

export function Overview() {
  const { t } = useTranslation('projects');

  const { project, openShareModal, canShare } = useOutletContext<ProjectDetailOutletContext>();
  const { onCreateBrief, onAddArtifacts, onUploadAssetsForApproval } = useProjectOverview(project);

  const projects = useProjectCapabilities();

  // the Summary card's "Create" CTA is an onboarding nudge —
  // hide it once the project has at least one brief. `pageSize=1` keeps the
  // payload minimal since we only need `total` from the paginated meta.
  // While loading / on error `briefsData?.total` is `undefined`, which we
  // forward as `hasBriefs={undefined}` — the widget hides the CTA in that
  // tri-state (see `ProjectSnapshotProps.hasBriefs`).
  const { data: briefsData } = useQuery(
    briefsQueryOptions({ projectId: project.platformProjectId, page: 1, pageSize: 1 }),
  );
  const hasBriefs = briefsData?.total === undefined ? undefined : briefsData.total > 0;

  return (
    <motion.div
      className="flex flex-1 flex-col gap-app-inset"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <StaggerItem index={1}>
        <GetStartedSoon
          projectId={project.platformProjectId}
          openComments={openCommentsOf(project)}
        />
      </StaggerItem>

      <StaggerItem index={2}>
        <div className="grid grid-cols-2 gap-app-inset">
          <ProjectSnapshot
            projectId={project.platformProjectId}
            status={project.status}
            title={project.title || t('projectDetail.overview.yourProject')}
            description={project.description}
            onCreateBrief={onCreateBrief}
            hasBriefs={hasBriefs}
          />
          <QuickStart
            onCreateBrief={onCreateBrief}
            onAddArtifacts={onAddArtifacts}
            onInviteCollaborators={openShareModal}
            onUploadAssetsForApproval={onUploadAssetsForApproval}
            createBriefDisabled={!projects.canEditDetails}
            addArtifactsDisabled={!projects.canUploadAssets}
            inviteDisabled={!canShare}
            uploadForApprovalDisabled={!projects.canUploadAssets}
          />
        </div>
      </StaggerItem>

      <StaggerItem index={3}>
        <DetailsSection project={project} />
      </StaggerItem>
    </motion.div>
  );
}
